import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { clsx } from "clsx";

export type ToastVariant = "info" | "success" | "warning" | "danger";

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

const variantClasses: Record<ToastVariant, string> = {
  info: "bg-[var(--color-surface-elevated)] border-[var(--color-border-default)] text-[var(--color-text-primary)]",
  success: "bg-[var(--color-success-100)] border-[var(--color-success-500)] text-[var(--color-neutral-900)]",
  warning: "bg-[var(--color-warning-100)] border-[var(--color-warning-500)] text-[var(--color-neutral-900)]",
  danger: "bg-[var(--color-danger-100)] border-[var(--color-danger-500)] text-[var(--color-neutral-900)]",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastVariant = "info") => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((t) => [...t, { id, message, variant }]);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) =>
      setTimeout(() => setToasts((cur) => cur.filter((c) => c.id !== t.id)), 4000),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 max-w-sm" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx(
              "px-4 py-3 rounded-md border shadow-[var(--shadow-md)] text-sm",
              variantClasses[t.variant],
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
